import React, { useEffect, useState } from "react";
import { Box, Dialog, Typography, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import commentsStar from "../../assets/Images/TempleDetail/comments-star.svg";
import { isUserLoggedIn } from "../../utils/authUtils";
import AuthDialog from "../../components/common/AuthDialog";
import { submitUserCampaignComment, getUserCampaignComments } from "../../api/services/campaignService";
import { T, FONT } from "./shared";

const buildImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  const base = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || "";
  return base ? `${base}${path.startsWith("/") ? "" : "/"}${path}` : path;
};

const timeAgo = (dateString) => {
  if (!dateString) return "";
  const s = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  const units = [
    [31536000, "year"],
    [2592000, "month"],
    [604800, "week"],
    [86400, "day"],
    [3600, "hour"],
    [60, "min"],
  ];
  for (const [secs, name] of units) {
    if (s >= secs) {
      const n = Math.floor(s / secs);
      return `${n} ${name}${n === 1 ? "" : "s"} ago`;
    }
  }
  return `${Math.max(s, 0)} sec ago`;
};

const toComment = (c, prefix) => ({
  id: `${prefix}-${c.id}`,
  author: c.username || "Anonymous",
  time: timeAgo(c.createdAt || c.updatedAt),
  content: c.message || "",
  avatar: c.profilepic ? buildImageUrl(c.profilepic) : null,
});

// Figma 3483:6607. Same behaviour as the campaign page's Comments: approved comments only,
// sign-in required to post, new comments wait for admin review.
const TempleComments = ({ campaignData }) => {
  const campaignId = campaignData?.id;
  const { user } = useSelector((state) => state.auth);
  const [text, setText] = useState("");
  const [userComments, setUserComments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Phones follow Figma "Mobile revamp" 739:17666
  const phone = useMediaQuery("(max-width:600px)");
  const [authOpen, setAuthOpen] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);

  const loadUserComments = async () => {
    try {
      const all = await getUserCampaignComments();
      setUserComments(Array.isArray(all) ? all.filter((c) => c.campaign_id === campaignId && c.status === 1) : []);
    } catch {
      setUserComments([]);
    }
  };

  useEffect(() => {
    if (campaignId) loadUserComments();
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const apiComments = Array.isArray(campaignData?.comments) ? campaignData.comments.filter((c) => c.status === 1) : [];
  const comments = [...userComments.map((c) => toComment(c, "user")), ...apiComments.map((c) => toComment(c, "api"))];

  const submit = async () => {
    if (!text.trim() || submitting) return;
    if (!isUserLoggedIn() || !user) {
      setAuthOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const data = { campaign_id: campaignId, user_id: user.id, username: user.name || "Anonymous", comments: text.trim(), status: 0 };
      if (user.profilePicture) {
        try {
          const blob = await (await fetch(buildImageUrl(user.profilePicture))).blob();
          data.profile_picture = new File([blob], "profile.jpg", { type: blob.type });
        } catch {
          // post without the picture
        }
      }
      const res = await submitUserCampaignComment(data);
      if (res && res.data) {
        setText("");
        await loadUserComments();
        setThanksOpen(true);
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
      alert("Failed to submit comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="section"
      id="comments"
      sx={{
        background: "#FFFFFF",
        border: phone ? "0.5px solid rgba(201,149,43,0.3)" : `1px solid ${T.sand}`,
        borderRadius: "20px",
        boxShadow: phone ? "0px 8px 24px 0px rgba(89,38,13,0.06)" : "none",
        p: { xs: "20px", sm: "28px" },
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        scrollMarginTop: "120px",
      }}
    >
      <Box sx={{ display: "flex", gap: phone ? "8px" : "10px", alignItems: "center" }}>
        {phone ? (
          <Typography aria-hidden="true" sx={{ fontFamily: "Inter, sans-serif", fontSize: "16px", lineHeight: "normal", color: T.red }}>✦</Typography>
        ) : (
          <Box component="img" src={commentsStar} alt="" sx={{ width: "18px", height: "18px" }} />
        )}
        <Typography component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "18px" : "22px", lineHeight: phone ? "normal" : "28px", color: T.ink }}>Comments</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: phone ? "column" : "row",
          alignItems: phone ? "stretch" : "center",
          justifyContent: "space-between",
          gap: "12px",
          minHeight: phone ? "112px" : "80px",
          p: phone ? "12px" : "16px",
          border: phone ? "1px solid #EFE4D3" : `1px solid ${T.sand}`,
          borderRadius: "12px",
          boxSizing: "border-box",
          "&:focus-within": { borderColor: T.gold },
        }}
      >
        <Box
          component="textarea"
          aria-label="Add a comment"
          placeholder="Add a comment"
          value={text}
          rows={phone ? 2 : 1}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || e.shiftKey || e.nativeEvent?.isComposing) return;
            e.preventDefault();
            submit();
          }}
          sx={{ flex: 1, minWidth: 0, p: 0, border: "none", outline: "none", resize: "none", background: "transparent", fontFamily: FONT, fontSize: phone ? "12px" : "15px", lineHeight: phone ? 1.5 : "22px", color: T.ink, "&::placeholder": { color: "#AAAAAA" } }}
        />
        <Box
          component="button"
          type="button"
          onClick={submit}
          disabled={submitting || !text.trim()}
          sx={{ alignSelf: phone ? "flex-end" : "auto", px: phone ? "16px" : "20px", py: phone ? "9px" : "12px", border: "none", borderRadius: phone ? "6px" : "10px", background: T.ink, color: "#FFFFFF", fontFamily: FONT, fontWeight: 700, fontSize: phone ? "10px" : "12px", lineHeight: phone ? "normal" : "16px", cursor: "pointer", flexShrink: 0, "&:disabled": { opacity: 0.6, cursor: "default" }, "&:focus-visible": { outline: `2px solid ${T.gold}`, outlineOffset: "2px" } }}
        >
          {submitting ? "SUBMITTING…" : "SUBMIT"}
        </Box>
      </Box>

      {comments.length === 0 ? (
        <Typography sx={{ fontFamily: FONT, fontSize: phone ? "12px" : "14px", lineHeight: phone ? 1.5 : "22px", color: "#AAAAAA", textAlign: "center" }}>No comments yet. Be the first to comment!</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {comments.map((c) => (
            <Box key={c.id} sx={{ display: "flex", gap: "12px", py: "14px", borderTop: `1px solid ${T.sand}` }}>
              <Box sx={{ width: "40px", height: "40px", borderRadius: "20px", background: T.cream, border: `1px solid ${T.sand}`, flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {c.avatar ? (
                  <Box component="img" src={c.avatar} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "14px", color: T.gold }}>{c.author.charAt(0).toUpperCase()}</Typography>
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "baseline", flexWrap: "wrap" }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "14px", color: T.ink }}>{c.author}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: "12px", color: T.muted }}>{c.time}</Typography>
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: "14px", lineHeight: "22px", color: T.bodyWarm, mt: "2px", wordBreak: "break-word" }}>{c.content}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={thanksOpen} onClose={() => setThanksOpen(false)} PaperProps={{ sx: { borderRadius: "18px", p: "24px", textAlign: "center", maxWidth: "360px" } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "18px", color: T.ink }}>Thank you for your comment!</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: "14px", color: T.muted, mt: "8px" }}>Your comment is under review by the admin.</Typography>
        <Box component="button" type="button" onClick={() => setThanksOpen(false)} sx={{ mt: "18px", alignSelf: "center", px: "22px", py: "10px", border: "none", borderRadius: "10px", background: T.red, color: "#FFFFFF", fontFamily: FONT, fontWeight: 700, cursor: "pointer" }}>
          Close
        </Box>
      </Dialog>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} initialView="login" />
    </Box>
  );
};

export default TempleComments;
